// ====== components/products/ProductBulkImport.js ======
// Version avec APERÇU AVANT IMPORT + Validation côté client

import React, { useState } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Form,
  Alert,
  ProgressBar,
  ListGroup,
  Badge,
  Spinner,
  Table
} from 'react-bootstrap';
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  FileText,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import productService from '../../services/productService';
import { useAuth } from '../../contexts/AuthContext';

const ProductBulkImport = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // ✅ NOUVEAUX ÉTATS pour l'aperçu
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [parsing, setParsing] = useState(false);

  // Vérification des permissions
  const canImport = user?.productAccessType === 'ALL';

  /**
   * ✅ NOUVELLE FONCTION : Parser le fichier Excel/CSV côté client
   */
  const parseFilePreview = async (file) => {
    setParsing(true);
    setError(null);
    setPreviewData(null);
    setValidationErrors([]);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      
      // Prendre la première feuille
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      
      // Convertir en JSON
      const jsonData = XLSX.utils.sheet_to_json(sheet, { 
        raw: false, // Garder les valeurs comme strings
        defval: '' // Valeur par défaut pour les cellules vides
      });


      if (jsonData.length === 0) {
        setError('Le fichier est vide ou mal formaté');
        setParsing(false);
        return;
      }

      // Validation des données
      const errors = validatePreviewData(jsonData);
      setValidationErrors(errors);

      // Préparer l'aperçu
      setPreviewData({
        rows: jsonData,
        totalRows: jsonData.length,
        validRows: jsonData.length - errors.length,
        invalidRows: errors.length
      });

    } catch (err) {
      console.error('❌ Erreur parsing:', err);
      setError('Erreur lors de la lecture du fichier. Vérifiez le format.');
    } finally {
      setParsing(false);
    }
  };

  /**
   * ✅ NOUVELLE FONCTION : Valider les données côté client avec messages détaillés
   */
  const validatePreviewData = (rows) => {
    const errors = [];
    const requiredColumns = ['name', 'price', 'categoryName', 'productType'];

    rows.forEach((row, index) => {
      const rowNumber = index + 2; // +2 car ligne 1 = header
      const rowErrors = [];

      // ✅ Vérifier les colonnes requises avec messages précis
      if (!row.name || String(row.name).trim() === '') {
        rowErrors.push('Le nom du produit est requis et ne peut pas être vide');
      } else if (String(row.name).trim().length < 2) {
        rowErrors.push('Le nom du produit doit contenir au moins 2 caractères');
      }

      if (!row.price || String(row.price).trim() === '') {
        rowErrors.push('Le prix est requis');
      } else {
        const price = parseFloat(row.price);
        if (isNaN(price)) {
          rowErrors.push(`Prix invalide: "${row.price}" n'est pas un nombre`);
        } else if (price <= 0) {
          rowErrors.push(`Prix invalide: ${price} (doit être supérieur à 0)`);
        }
      }

      if (!row.categoryName || String(row.categoryName).trim() === '') {
        rowErrors.push('La catégorie est requise');
      }

      if (!row.productType || String(row.productType).trim() === '') {
        rowErrors.push('Le type de produit est requis');
      } else {
        const validTypes = ['STOCKABLE', 'SERVICE'];
        const typeUpper = String(row.productType).toUpperCase();
        if (!validTypes.includes(typeUpper)) {
          rowErrors.push(
            `Type de produit invalide: "${row.productType}". Utilisez "STOCKABLE" ou "SERVICE"`
          );
        }
      }

      // ✅ Validation du stock pour produits stockables
      if (row.productType && String(row.productType).toUpperCase() === 'STOCKABLE') {
        if (row.initialStock) {
          const stock = parseInt(row.initialStock);
          if (isNaN(stock)) {
            rowErrors.push(`Stock initial invalide: "${row.initialStock}" n'est pas un nombre`);
          } else if (stock < 0) {
            rowErrors.push(`Stock initial invalide: ${stock} (ne peut pas être négatif)`);
          }
        }
      }

      // ✅ Validation du prix de revient
      if (row.costPrice) {
        const costPrice = parseFloat(row.costPrice);
        if (isNaN(costPrice)) {
          rowErrors.push(`Prix de revient invalide: "${row.costPrice}" n'est pas un nombre`);
        } else if (costPrice < 0) {
          rowErrors.push(`Prix de revient invalide: ${costPrice} (ne peut pas être négatif)`);
        }
      }

      // ✅ Validation des seuils
      if (row.minThreshold || row.maxThreshold) {
        const minThreshold = row.minThreshold ? parseInt(row.minThreshold) : null;
        const maxThreshold = row.maxThreshold ? parseInt(row.maxThreshold) : null;

        if (row.minThreshold && isNaN(minThreshold)) {
          rowErrors.push(`Seuil minimum invalide: "${row.minThreshold}" n'est pas un nombre`);
        } else if (minThreshold !== null && minThreshold < 0) {
          rowErrors.push(`Seuil minimum invalide: ${minThreshold} (ne peut pas être négatif)`);
        }

        if (row.maxThreshold && isNaN(maxThreshold)) {
          rowErrors.push(`Seuil maximum invalide: "${row.maxThreshold}" n'est pas un nombre`);
        } else if (maxThreshold !== null && maxThreshold < 0) {
          rowErrors.push(`Seuil maximum invalide: ${maxThreshold} (ne peut pas être négatif)`);
        }

        if (
          minThreshold !== null &&
          maxThreshold !== null &&
          !isNaN(minThreshold) &&
          !isNaN(maxThreshold) &&
          minThreshold >= maxThreshold
        ) {
          rowErrors.push(
            `Seuils incohérents: minimum (${minThreshold}) doit être < maximum (${maxThreshold})`
          );
        }
      }

      // ✅ Validation SKU (si fourni)
      if (row.sku && String(row.sku).trim().length > 0) {
        const sku = String(row.sku).trim();
        if (sku.length < 2) {
          rowErrors.push('Le SKU doit contenir au moins 2 caractères');
        }
      }

      // ✅ Validation code-barres (si fourni)
      if (row.barcode && String(row.barcode).trim().length > 0) {
        const barcode = String(row.barcode).trim();
        if (barcode.length < 4) {
          rowErrors.push('Le code-barres doit contenir au moins 4 caractères');
        }
      }

      if (rowErrors.length > 0) {
        errors.push({
          rowNumber,
          productName: row.name || 'Sans nom',
          errors: rowErrors
        });
      }
    });

    return errors;
  };

  /**
   * Gestion de la sélection de fichier
   */
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    await validateAndSetFile(file);
  };

  /**
   * Gestion du drag & drop
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  /**
   * Validation du fichier
   */
  const validateAndSetFile = async (file) => {
    if (!file) return;

    // Validation du type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    if (!validTypes.includes(file.type)) {
      setError('Format de fichier invalide. Utilisez .xlsx, .xls ou .csv');
      return;
    }

    // Validation de la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Le fichier est trop volumineux (max 5MB)');
      return;
    }

    setError(null);
    setSelectedFile(file);
    setImportResult(null);

    // ✅ Parser automatiquement pour l'aperçu
    await parseFilePreview(file);
  };

  /**
   * Télécharger le modèle
   */
  const downloadTemplate = () => {
    const headers = [
      'name',
      'price',
      'categoryName',
      'productType',
      'description',
      'sku',
      'barcode',
      'costPrice',
      'initialStock',
      'minThreshold',
      'maxThreshold'
    ].join(',');

    const exampleRow = [
      'Chemise Bleue',
      '25000',
      'Vêtements',
      'STOCKABLE',
      'Belle chemise en coton',
      'CHM001',
      '1234567890',
      '15000',
      '50',
      '5',
      '100'
    ].join(',');

    const csvContent = `${headers}\n${exampleRow}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'modele_import_produits.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Lancer l'import (après validation de l'aperçu)
   */
  const handleImport = async () => {
    if (!selectedFile) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    // Bloquer si des erreurs de validation
    if (validationErrors.length > 0) {
      setError('Veuillez corriger les erreurs avant d\'importer');
      return;
    }

    setImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const response = await productService.importProducts(selectedFile);

      if (response.success) {
        setImportResult(response.data);
      } else {
        setError(response.message || 'Erreur lors de l\'import');
      }
    } catch (err) {
      console.error('❌ Erreur import:', err);
      setError('Erreur de connexion lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  /**
   * Réinitialiser
   */
  const handleReset = () => {
    setSelectedFile(null);
    setImportResult(null);
    setError(null);
    setPreviewData(null);
    setValidationErrors([]);
  };

  /**
   * Formater la taille du fichier
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Si pas de permission
  if (!canImport) {
    return (
      <Container className="mt-4">
        <Card>
          <Card.Body className="text-center py-5">
            <AlertCircle size={48} className="text-warning mb-3" />
            <h5>Accès Refusé</h5>
            <p className="text-muted">
              Vous devez avoir un accès complet (ALL) pour importer des produits en masse.
            </p>
            <Button variant="outline-primary" onClick={() => navigate('/products')}>
              <ArrowLeft size={16} className="me-2" />
              Retour à la liste
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* En-tête */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h3 className="mb-1">
                <FileSpreadsheet className="me-2" />
                Import Multiple de Produits
              </h3>
              <p className="text-muted">
                Importez plusieurs produits à la fois via un fichier Excel ou CSV
              </p>
            </div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/products')}
            >
              <ArrowLeft size={16} className="me-2" />
              Retour
            </Button>
          </div>
        </Col>
      </Row>

      {/* Instructions */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card>
            <Card.Body>
              <h5 className="mb-3">
                <FileText className="me-2" />
                Instructions
              </h5>
              <ol className="mb-3">
                <li>Téléchargez le modèle de fichier ci-dessous</li>
                <li>Remplissez-le avec vos produits</li>
                <li>Importez le fichier complété</li>
                <li className="text-primary"><strong>Vérifiez l'aperçu avant de confirmer l'import</strong></li>
              </ol>

              <Alert variant="info" className="mb-3">
                <strong>Colonnes requises:</strong>
                <ul className="mb-0 mt-2">
                  <li><code>name</code> - Nom du produit</li>
                  <li><code>price</code> - Prix de vente (FCFA)</li>
                  <li><code>categoryName</code> - Nom de la catégorie</li>
                  <li><code>productType</code> - STOCKABLE ou SERVICE</li>
                </ul>
              </Alert>

              <Button
                variant="primary"
                onClick={downloadTemplate}
                className="me-2"
              >
                <Download size={16} className="me-2" />
                Télécharger le modèle
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card>
            <Card.Body>
              <h6 className="mb-3">Formats acceptés</h6>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <FileSpreadsheet size={16} className="me-2" />
                  Excel (.xlsx, .xls)
                </ListGroup.Item>
                <ListGroup.Item>
                  <FileText size={16} className="me-2" />
                  CSV (.csv)
                </ListGroup.Item>
              </ListGroup>
              <div className="mt-3 text-muted small">
                Taille maximale: 5 MB
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Zone d'upload */}
      {!importResult && !previewData && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Body>
                <div
                  className={`border rounded p-5 text-center ${
                    dragActive ? 'border-primary bg-light' : 'border-dashed'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <Upload size={48} className="text-muted mb-3" />

                  {parsing ? (
                    <div>
                      <Spinner animation="border" className="mb-2" />
                      <p className="text-muted">Analyse du fichier en cours...</p>
                    </div>
                  ) : selectedFile ? (
                    <div>
                      <h5 className="text-success mb-2">
                        <CheckCircle size={20} className="me-2" />
                        Fichier sélectionné
                      </h5>
                      <p className="mb-2">{selectedFile.name}</p>
                      <p className="text-muted small">
                        {formatFileSize(selectedFile.size)}
                      </p>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReset();
                        }}
                      >
                        Changer de fichier
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <h5>Glissez-déposez votre fichier ici</h5>
                      <p className="text-muted">ou cliquez pour sélectionner</p>
                    </div>
                  )}

                  <Form.Control
                    id="fileInput"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </div>

                {error && (
                  <Alert variant="danger" className="mt-3">
                    {error}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* ✅ NOUVEAU : Aperçu des données */}
      {previewData && !importResult && (
        <Row className="mb-4">
          <Col>
            <Card className={validationErrors.length > 0 ? 'border-warning' : 'border-info'}>
              <Card.Header className="bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <Eye size={20} className="me-2" />
                    Aperçu des données
                  </h5>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleReset}
                  >
                    Annuler
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {/* Statistiques */}
                <Row className="mb-4">
                  <Col md={4}>
                    <Card className="bg-primary text-white">
                      <Card.Body className="text-center">
                        <h2>{previewData.totalRows}</h2>
                        <div>Total lignes</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="bg-success text-white">
                      <Card.Body className="text-center">
                        <h2>{previewData.validRows}</h2>
                        <div>Lignes valides</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="bg-danger text-white">
                      <Card.Body className="text-center">
                        <h2>{previewData.invalidRows}</h2>
                        <div>Lignes avec erreurs</div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* ✅ Erreurs de validation - AFFICHAGE DÉTAILLÉ */}
                {validationErrors.length > 0 && (
                  <Alert variant="danger" className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <AlertTriangle size={24} className="me-2" />
                      <strong className="fs-5">
                        {validationErrors.length} erreur(s) de validation détectée(s)
                      </strong>
                    </div>
                    <p className="mb-3">
                      ⚠️ <strong>Corrigez ces erreurs dans votre fichier avant de pouvoir importer :</strong>
                    </p>
                    <ListGroup style={{ maxHeight: '350px', overflowY: 'auto' }}>
                      {validationErrors.map((error, index) => (
                        <ListGroup.Item key={index} variant="danger" className="border-danger">
                          <div className="mb-2">
                            <Badge bg="danger" className="me-2 fs-6">
                              📍 Ligne {error.rowNumber}
                            </Badge>
                            <strong className="text-danger">
                              {error.productName || 'Produit sans nom'}
                            </strong>
                          </div>
                          
                          {/* Liste détaillée des erreurs pour cette ligne */}
                          <div className="ms-4 mt-2">
                            {error.errors.map((err, i) => (
                              <div key={i} className="d-flex align-items-start mb-2">
                                <XCircle size={16} className="text-danger me-2 mt-1 flex-shrink-0" />
                                <span className="text-dark">
                                  <strong>Erreur :</strong> {err}
                                </span>
                              </div>
                            ))}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                    
                    {/* Instructions de correction */}
                    <div className="mt-3 p-3 bg-light rounded">
                      <strong>💡 Comment corriger :</strong>
                      <ol className="mb-0 mt-2 small">
                        <li>Ouvrez votre fichier Excel/CSV</li>
                        <li>Corrigez les erreurs mentionnées ci-dessus</li>
                        <li>Sauvegardez le fichier</li>
                        <li>Importez-le à nouveau</li>
                      </ol>
                    </div>
                  </Alert>
                )}

                {/* Tableau d'aperçu avec indicateurs d'erreurs */}
                <div className="mb-3">
                  <h6>📊 Aperçu des données ({previewData.rows.length} lignes)</h6>
                  <small className="text-muted">
                    Faites défiler pour voir toutes les lignes. Les lignes en rouge contiennent des erreurs.
                  </small>
                </div>
                
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm">
                    <thead className="table-dark sticky-top">
                      <tr>
                        <th style={{ width: '50px' }}>#</th>
                        <th>Nom</th>
                        <th>Prix</th>
                        <th>Catégorie</th>
                        <th>Type</th>
                        <th>Stock</th>
                        <th>SKU</th>
                        <th style={{ width: '120px' }}>État</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.rows.map((row, index) => {
                        const hasError = validationErrors.find(e => e.rowNumber === index + 2);
                        return (
                          <tr 
                            key={index} 
                            className={hasError ? 'table-danger' : 'table-success'}
                            style={{ 
                              backgroundColor: hasError ? '#f8d7da' : '#d1e7dd'
                            }}
                          >
                            <td className="text-center">
                              <strong>{index + 1}</strong>
                            </td>
                            <td>
                              {row.name || <span className="text-danger">❌ Manquant</span>}
                              {hasError && (
                                <div className="small text-danger mt-1">
                                  {hasError.errors.slice(0, 2).map((err, i) => (
                                    <div key={i}>• {err}</div>
                                  ))}
                                  {hasError.errors.length > 2 && (
                                    <div className="fst-italic">
                                      + {hasError.errors.length - 2} autre(s) erreur(s)
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td>
                              {row.price ? (
                                `${row.price} FCFA`
                              ) : (
                                <span className="text-danger">❌ Manquant</span>
                              )}
                            </td>
                            <td>{row.categoryName || <span className="text-danger">❌ Manquant</span>}</td>
                            <td>
                              {row.productType ? (
                                <Badge bg={row.productType === 'STOCKABLE' ? 'primary' : 'info'}>
                                  {row.productType}
                                </Badge>
                              ) : (
                                <span className="text-danger">❌ Manquant</span>
                              )}
                            </td>
                            <td className="text-center">{row.initialStock || '-'}</td>
                            <td><code>{row.sku || '-'}</code></td>
                            <td className="text-center">
                              {hasError ? (
                                <Badge bg="danger" className="w-100">
                                  <XCircle size={14} className="me-1" />
                                  {hasError.errors.length} erreur(s)
                                </Badge>
                              ) : (
                                <Badge bg="success" className="w-100">
                                  <CheckCircle size={14} className="me-1" />
                                  Valide
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Boutons d'action avec messages clairs */}
                <div className="text-center mt-4">
                  {validationErrors.length > 0 ? (
                    <div>
                      <Button
                        variant="danger"
                        size="lg"
                        disabled
                        className="mb-3"
                      >
                        <XCircle size={20} className="me-2" />
                        Import bloqué - {validationErrors.length} erreur(s) à corriger
                      </Button>
                      <div className="alert alert-danger d-inline-block">
                        <strong>⚠️ Impossible d'importer</strong>
                        <br />
                        Corrigez les {validationErrors.length} erreur(s) ci-dessus dans votre fichier, 
                        puis importez-le à nouveau.
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="success"
                      size="lg"
                      onClick={handleImport}
                      disabled={importing}
                      className="mb-3"
                    >
                      {importing ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Import en cours...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={20} className="me-2" />
                          ✅ Confirmer l'import de {previewData.validRows} produits
                        </>
                      )}
                    </Button>
                  )}
                  
                  <div className="mt-2">
                    <Button
                      variant="outline-secondary"
                      onClick={handleReset}
                      disabled={importing}
                    >
                      Annuler et choisir un autre fichier
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Résultats de l'import */}
      {importResult && (
        <Row>
          <Col>
            <Card className="border-success">
              <Card.Body>
                <h5 className="text-success mb-4">
                  <CheckCircle size={24} className="me-2" />
                  Import Terminé
                </h5>

                {/* Statistiques */}
                <Row className="mb-4">
                  <Col md={4}>
                    <Card className="bg-success text-white">
                      <Card.Body className="text-center">
                        <h2>{importResult.success_count}</h2>
                        <div>Réussis</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="bg-danger text-white">
                      <Card.Body className="text-center">
                        <h2>{importResult.failure_count}</h2>
                        <div>Échecs</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="bg-primary text-white">
                      <Card.Body className="text-center">
                        <h2>{importResult.total_count}</h2>
                        <div>Total</div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Barre de progression */}
                {importResult.total_count > 0 && (
                  <div className="mb-4">
                    <ProgressBar>
                      <ProgressBar
                        variant="success"
                        now={(importResult.success_count / importResult.total_count) * 100}
                        label={`${importResult.success_count} réussis`}
                      />
                      <ProgressBar
                        variant="danger"
                        now={(importResult.failure_count / importResult.total_count) * 100}
                        label={`${importResult.failure_count} échecs`}
                      />
                    </ProgressBar>
                  </div>
                )}

                {/* Liste des erreurs */}
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-danger mb-3">
                      <XCircle size={20} className="me-2" />
                      Erreurs détectées ({importResult.errors.length})
                    </h6>
                    <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {importResult.errors.map((error, index) => (
                        <ListGroup.Item key={index} variant="danger">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <Badge bg="danger" className="me-2">
                                Ligne {error.row_number}
                              </Badge>
                              <strong>{error.product_name}</strong>
                              <div className="text-muted small mt-1">
                                {error.error_message}
                              </div>
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}

                {/* Produits importés */}
                {importResult.importedProducts && importResult.importedProducts.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-success mb-3">
                      <CheckCircle size={20} className="me-2" />
                      Produits importés avec succès ({importResult.importedProducts.length})
                    </h6>
                    <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {importResult.importedProducts.map((product, index) => (
                        <ListGroup.Item key={index}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{product.name}</strong>
                              <div className="text-muted small">
                                {product.categoryName} • {product.price} FCFA
                                {product.sku && ` • SKU: ${product.sku}`}
                              </div>
                            </div>
                            <Badge bg={product.productType === 'STOCKABLE' ? 'primary' : 'info'}>
                              {product.productType}
                            </Badge>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}

                {/* Actions */}
                <div className="text-center">
                  <Button
                    variant="primary"
                    className="me-2"
                    onClick={() => navigate('/products')}
                  >
                    Voir tous les produits
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={handleReset}
                  >
                    Importer d'autres produits
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ProductBulkImport;